import { Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft, Bus, CalendarDays, Clock, ExternalLink, Euro, Flame, MapPin, Mountain, Phone, Ruler, Snowflake, TramFront, UtensilsCrossed, Waves } from "lucide-react";

import { getPublicHubPlace, type HubDTO, type HubPlaceDTO, type HubProductDTO } from "@/lib/hubs.functions";
import { SmartImage } from "@/components/ui/SmartImage";
import { usePublicCities } from "@/hooks/use-public-cities";
import { computeOpenStatus, type HoursSource } from "@/lib/opening-hours";

export const hubPlaceQuery = (hubSlug: string, placeSlug: string) =>
  queryOptions({
    queryKey: ["public-hub-place", hubSlug, placeSlug],
    queryFn: () => getPublicHubPlace({ data: { hubSlug, placeSlug } }),
  });


const DAY_LABELS: Record<string, { en: string; fi: string }> = {
  mon: { en: "Monday", fi: "Maanantai" },
  tue: { en: "Tuesday", fi: "Tiistai" },
  wed: { en: "Wednesday", fi: "Keskiviikko" },
  thu: { en: "Thursday", fi: "Torstai" },
  fri: { en: "Friday", fi: "Perjantai" },
  sat: { en: "Saturday", fi: "Lauantai" },
  sun: { en: "Sunday", fi: "Sunnuntai" },
};

/** Turn the stored opening-hours JSON into printable day/value rows. */
function formatOpeningHours(raw: unknown, locale: "en" | "fi"): { day: string; value: string }[] {
  if (!raw || typeof raw !== "object") return [];
  const obj = raw as Record<string, any>;
  const out: { day: string; value: string }[] = [];
  for (const [key, label] of Object.entries(DAY_LABELS)) {
    const entry = obj[key] ?? obj[label.en] ?? obj[label.en.toLowerCase()];
    if (!entry) continue;
    const day = locale === "fi" ? label.fi : label.en;
    if (typeof entry === "string") {
      out.push({ day, value: entry });
    } else if (entry.closed) {
      out.push({ day, value: locale === "fi" ? "Suljettu" : "Closed" });
    } else if (entry.open && entry.close) {
      out.push({ day, value: `${entry.open}–${entry.close}` });
    }
  }
  return out;
}

type Props = { hubSlug: string; placeSlug: string; locale: "en" | "fi" };

export function HubPlacePage({ hubSlug, placeSlug, locale }: Props) {
  const { data } = useSuspenseQuery(hubPlaceQuery(hubSlug, placeSlug)) as {
    data: {
      hub: HubDTO;
      place: HubPlaceDTO;
      products: HubProductDTO[];
      siblings: { slug: string; name: string; image_url: string | null }[];
    } | null;
  };
  const citiesQuery = usePublicCities() as any;
  const cities: { slug: string; name: string }[] = citiesQuery?.data ?? citiesQuery ?? [];

  if (!data) return null;
  const { hub, place, products, siblings } = data;
  const base = locale === "fi" ? `/fi/${hub.slug}` : `/${hub.slug}`;
  const hubName = locale === "fi" ? hub.name_fi || hub.name : hub.name;

  const a = (k: string) => {
    const v = place.attributes?.[k];
    return v === null || v === undefined || v === "" ? null : String(v);
  };

  // Only link to slugs that are real published destinations — a place's
  // city_slug can be a municipality with no destination page of its own.
  const cityChips = place.cities
    .map((slug) => ({ slug, city: cities.find((c) => c.slug === slug) }))
    .filter((c): c is { slug: string; city: { slug: string; name: string } } => Boolean(c.city))
    .map((c) => ({ slug: c.slug, name: c.city.name }))
    .filter((c, i, arr) => arr.findIndex((x) => x.slug === c.slug) === i);


  const cuisine = a("cuisine") ?? place.subcategory ?? place.category;
  const priceRange = a("price_range") ?? place.price;
  const hours = formatOpeningHours(place.opening_hours, locale);

  const facts = [
    cuisine && { icon: UtensilsCrossed, label: locale === "fi" ? "Keittiö" : "Cuisine", value: cuisine },
    priceRange && { icon: Euro, label: locale === "fi" ? "Hintataso" : "Price range", value: priceRange },
    place.phone && { icon: Phone, label: locale === "fi" ? "Puhelin" : "Phone", value: place.phone },
    a("municipality") && { icon: MapPin, label: locale === "fi" ? "Kunta" : "Municipality", value: a("municipality")! },
    a("region") && { icon: MapPin, label: locale === "fi" ? "Maakunta" : "Region", value: a("region")! },
    a("established") && {
      icon: CalendarDays,
      label: locale === "fi" ? "Perustettu" : "Established",
      value: a("established")!,
    },
    a("area_km2") && { icon: Ruler, label: locale === "fi" ? "Pinta-ala" : "Area", value: `${a("area_km2")} km²` },
    a("sauna_type") && {
      icon: Flame,
      label: locale === "fi" ? "Saunatyyppi" : "Sauna type",
      value: a("sauna_type")!,
    },
    a("swim") && { icon: Waves, label: locale === "fi" ? "Uinti" : "Swim", value: a("swim")! },
    a("lifts") && { icon: TramFront, label: locale === "fi" ? "Hissit" : "Lifts", value: a("lifts")! },
    a("slopes") && { icon: Mountain, label: locale === "fi" ? "Rinteet" : "Slopes", value: a("slopes")! },
    a("vertical_drop") && {
      icon: Ruler,
      label: locale === "fi" ? "Korkeusero" : "Vertical drop",
      value: a("vertical_drop")!,
    },
    a("season") && { icon: Snowflake, label: locale === "fi" ? "Kausi" : "Season", value: a("season")! },
    a("public_transport") && {
      icon: Bus,
      label: locale === "fi" ? "Julkisilla" : "By public transport",
      value: a("public_transport")!,
    },
  ].filter(Boolean) as { icon: typeof MapPin; label: string; value: string }[];

  return (
    <main className="min-h-screen bg-background">
      <div className="relative h-[38vh] min-h-[220px] w-full overflow-hidden bg-muted">
        {place.image_url && (
          <SmartImage src={place.image_url} alt={place.name} className="h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-5xl px-4 pb-6">
          <Link to={base as any} className="inline-flex items-center gap-1 text-xs text-white/90 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> {hubName}
          </Link>
        </div>
        {a("photo_credit") && (
          <span className="absolute bottom-1 right-2 text-[10px] text-white/60">
            {locale === "fi" ? "Kuva" : "Photo"}: {a("photo_credit")}
          </span>
        )}
      </div>

      <div className="mx-auto max-w-5xl px-4 py-8">
        <header className="mb-8 border-b border-border/60 pb-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="space-y-4">
              <h1 className="font-heading text-4xl font-extrabold leading-none tracking-tight text-primary sm:text-6xl">
                {place.name}
              </h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <PlaceStatusPill
                  source={{
                    schedule_info: (place as any).schedule_info ?? null,
                    opening_hours: place.opening_hours as never,
                    special_hours: place.special_hours,
                    temporarily_closed: place.temporarily_closed,
                  }}
                  locale={locale}
                />
              </div>
            </div>
            {place.menu_url || (place as any).has_dish_menu ? (
              <div className="shrink-0">
                <Link
                  to="/menu/$slug"
                  params={{ slug: place.slug }}
                  className="group inline-flex items-center gap-3 rounded-full bg-primary px-7 py-3.5 text-primary-foreground transition-all duration-300 hover:shadow-xl active:scale-95"
                >
                  <UtensilsCrossed className="h-4 w-4 opacity-70 transition-transform duration-300 group-hover:rotate-12" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em]">
                    {locale === "fi" ? "Ruokalista" : "View menu"}
                  </span>
                </Link>
              </div>
            ) : null}
          </div>
          {(a("municipality") || place.area || cuisine) && (
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              {[a("municipality") ?? place.area, cuisine].filter(Boolean).map((v) => (
                <span key={v as string}>{v}</span>
              ))}
            </div>
          )}
        </header>
        {cityChips.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              {locale === "fi" ? "Kohteissa" : "Also in"}
            </span>
            {cityChips.map((c) => (
              <Link
                key={c.slug}
                to={(locale === "fi" ? "/fi/$citySlug" : "/$citySlug") as any}
                params={{ citySlug: c.slug } as any}
                className="rounded-full border border-border px-3 py-1 text-xs capitalize hover:bg-muted"
              >
                {c.name}
              </Link>
            ))}
          </div>
        )}

        {facts.length > 0 && (
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {facts.map((f) => (
              <div key={f.label} className="rounded-xl border border-border bg-card p-3">
                <dt className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <f.icon className="h-3.5 w-3.5" /> {f.label}
                </dt>
                <dd className="mt-1 text-sm">{f.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {hours.length > 0 && (
          <section className="mt-8">
            <h2 className="font-heading flex items-center gap-2 text-xl">
              <Clock className="h-4 w-4 text-primary" /> {locale === "fi" ? "Aukioloajat" : "Opening hours"}
            </h2>
            <dl className="mt-3 grid max-w-md grid-cols-1 gap-1 text-sm">
              {hours.map((h) => (
                <div key={h.day} className="flex justify-between border-b border-border/60 py-1">
                  <dt className="text-muted-foreground">{h.day}</dt>
                  <dd>{h.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {place.menu_url && (
          <Link
            to="/menu/$slug"
            params={{ slug: place.slug }}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
          >
            <UtensilsCrossed className="h-4 w-4" /> {locale === "fi" ? "Katso ruokalista" : "View menu"}
          </Link>
        )}

        {place.description && (
          <section className="mt-8">
            <h2 className="font-heading text-xl">{locale === "fi" ? "Yleistä" : "Overview"}</h2>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
              {place.description}
            </p>
          </section>
        )}

        {products.length > 0 && (
          <section className="mt-10">
            <h2 className="font-heading text-xl">{locale === "fi" ? "Varattavat tuotteet" : "Book a tour"}</h2>
            <ul className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {products.map((pr) => {
                const t = locale === "fi" ? pr.title_fi || pr.title : pr.title;
                const d = locale === "fi" ? pr.description_fi || pr.description : pr.description;
                const href = pr.bokun_widget_url || pr.external_url || undefined;
                return (
                  <li key={pr.id} className="overflow-hidden rounded-2xl border border-border bg-card">
                    {pr.image_url && (
                      <SmartImage src={pr.image_url} alt={t} className="h-40 w-full object-cover" />
                    )}
                    <div className="p-4">
                      <h3 className="font-heading text-base">{t}</h3>
                      {d && <p className="mt-1 text-sm text-muted-foreground">{d}</p>}
                      <div className="mt-3 flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {[pr.duration, pr.price_from].filter(Boolean).join(" · ")}
                        </span>
                        {href && (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-bokun-widget-id={pr.bokun_product_id ?? undefined}
                            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs text-primary-foreground"
                          >
                            {locale === "fi" ? "Varaa" : "Book"} <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {siblings.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-xl">{locale === "fi" ? "Lisää kohteita" : `More in ${hubName}`}</h2>
            <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {siblings.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`${base}/$slug` as any}
                    params={{ slug: s.slug } as any}
                    className="block overflow-hidden rounded-xl border border-border bg-card"
                  >
                    <div className="aspect-[4/3] bg-muted">
                      {s.image_url && <SmartImage src={s.image_url} alt={s.name} className="h-full w-full object-cover" />}
                    </div>
                    <span className="block p-2 text-xs">{s.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
}

function PlaceStatusPill({ source, locale }: { source: HoursSource; locale: "en" | "fi" }) {
  const status = computeOpenStatus(source);
  if (status.state === "unknown" && status.todayLabel === "—") return null;
  const open = status.state === "open" || status.state === "closing_soon";
  const label = open
    ? status.state === "closing_soon"
      ? locale === "fi"
        ? "Sulkeutuu pian"
        : "Closing soon"
      : locale === "fi"
        ? "Avoinna nyt"
        : "Open now"
    : locale === "fi"
      ? "Suljettu"
      : "Closed";
  return (
    <>
      <span
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 ${
          open ? "border-emerald-100 bg-emerald-50" : "border-border bg-muted"
        }`}
      >
        <span className="relative flex h-2 w-2">
          {open && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          )}
          <span
            className={`relative inline-flex h-2 w-2 rounded-full ${open ? "bg-emerald-500" : "bg-muted-foreground/50"}`}
          />
        </span>
        <span
          className={`text-[10px] font-bold uppercase tracking-[0.12em] ${
            open ? "text-emerald-800" : "text-muted-foreground"
          }`}
        >
          {label}
        </span>
      </span>
      {status.todayLabel && status.todayLabel !== "—" ? (
        <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <span className="h-1 w-1 rounded-full bg-border" />
          <span style={{ textTransform: "none" }}>
            {locale === "fi" ? "Tänään" : "Today"}: {status.todayLabel}
          </span>
        </span>
      ) : null}
    </>
  );
}
